import React, { useId, useState, useCallback, MouseEvent, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { Icon } from '@iconify/react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputLabel,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';
import i18next from 'i18next';
import { useLocation } from 'react-router-dom';
import { borderTop, minWidth } from '@mui/system';
import { ReactComponent as GlobeIcon } from '../../assets/icons/globe.svg';
import Badge from '../../components/Badge/Badge';
import { ReactComponent as Logo } from '../../assets/images/logo.svg';
import NavSlidein from './NavSlideIn';
import NotificationMenu from './NotificationMenu';
import SearchBar from './Search';
import ProfileMenu from './ProfileMenu';
import LocalizationMenu from './LocalizationMenu';
import { ILanguage } from '../../types/language';
import { getLanguages } from '../../services/language';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import TextField from '../../components/TextField/TextField';
import { notifications } from '../../services/vehicles';
import { setNotificationCount } from '../../redux/slices/notificationSlice';
import ProxyLoginDialog from './ProxyLoginDialog';
import { useThemeContext } from '../../context/ThemeContext';
import MarketSelector from './MarketSelector';

const Header = () => {
  // use hooks
  const dispatch = useAppDispatch();
  const location = useLocation();

  // local states
  const [notificationMenuAnchorEl, setNotificationMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [localizationMenuAnchorEl, setLocalizationMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isSideNavOpen, setIsSideNavOpen] = useState<boolean>(false);
  const [language, setLanguage] = useState('en');
  const [languages, setLanguages] = useState<ILanguage[]>([]);
  const [isProxyDialogOpen, setIsProxyDialogOpen] = React.useState(false);

  // global states
  const { count } = useAppSelector((state) => state.notification);
  const { userData } = useAppSelector((state) => state?.user);

  // constants
  const notificationMenuId = useId();
  const profileMenuId = useId();
  const localizationMenuId = useId();
  // constants
  const oktaStorage = window?.localStorage?.getItem?.('okta-token-storage');
  const userInfo = oktaStorage ? JSON.parse(oktaStorage)?.idToken?.claims : null;

  const handleClickOpen = () => {
    setIsProxyDialogOpen(true);
  };

  const handleProxyDialogClose = () => {
    setIsProxyDialogOpen(false);
  };

  const { mode, toggleMode } = useThemeContext();
  const isDarkMode = mode === 'dark';

  // handling menu open - -
  const handleNotificationMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setNotificationMenuAnchorEl(event.currentTarget);
  }, []);

  const handleProfileMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setProfileMenuAnchorEl(event.currentTarget);
  }, []);

  const handleLocalizationMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setLocalizationMenuAnchorEl(event.currentTarget);
  }, []);

  // handling menu close - -
  const handleMenuClose = useCallback(() => {
    setNotificationMenuAnchorEl(null);
  }, []);

  const handleProfileMenuClose = useCallback(() => {
    setProfileMenuAnchorEl(null);
  }, []);

  const handleLocalizationMenuClose = useCallback(() => {
    setLocalizationMenuAnchorEl(null);
  }, []);

  // fetching notification count
  const getNotificationCount = useCallback(async () => {
    await notifications({
      method: 'GET',
      url: 'notificationCount',
      params: {
        isActive: true,
      },
    }).then((resp) => {
      dispatch(setNotificationCount(resp?.data?.data));
    });
  }, [dispatch]);

  // handling language switch
  const handleSelectLanguage = useCallback((code: string) => {
    i18next.changeLanguage(code);
    setLanguage(code);
    const htmlElement = document.querySelector('html');
    const bodyElement = document.querySelector('body');
    if (htmlElement) {
      htmlElement.lang = code;
    }
    if (bodyElement) {
      if (code === 'ar') {
        bodyElement.dir = 'rtl';
      } else {
        bodyElement.dir = 'ltr';
      }
    }
    setLocalizationMenuAnchorEl(null);
  }, []);

  // lifecycle hooks
  useEffect(() => {
    setLanguage(i18next.language);
  }, []);

  // useEffect(() => {
  //   getLanguages().then((response) => {
  //     setLanguages(response);
  //   });
  //   getNotificationCount();
  // }, [getNotificationCount]);

  return (
    <>
      {/* <NotificationMenu id={notificationMenuId} anchorEl={notificationMenuAnchorEl} handleMenuClose={handleMenuClose} /> */}
      <ProfileMenu
        id={profileMenuId}
        anchorEl={profileMenuAnchorEl}
        handleMenuClose={handleProfileMenuClose}
        handleClickOpen={handleClickOpen}
      />
      <LocalizationMenu
        id={localizationMenuId}
        anchorEl={localizationMenuAnchorEl}
        handleMenuClose={handleLocalizationMenuClose}
        handleSelectLanguage={handleSelectLanguage}
        languages={languages}
      />
      <NavSlidein isOpen={isSideNavOpen} setIsOpen={setIsSideNavOpen} currentPath={location.pathname} />
      <ProxyLoginDialog open={isProxyDialogOpen} onClose={handleProxyDialogClose} />
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: '#000',
          height: 57,
          borderBottom: '2px solid #333',
          boxShadow: 'none',
        }}
      >
        <Stack direction="row" height={55} alignItems="center" justifyContent="center" px={2}>
          {location.pathname !== '/v360-app/' && (
            <IconButton
              size="small"
              edge="start"
              color="inherit"
              aria-label="open drawer"
              sx={{ mr: 3, color: '#fff', '&:hover': { backgroundColor: '#222' } }}
              onClick={() => setIsSideNavOpen((prev) => !prev)}
            >
              <Icon icon="cil:hamburger-menu" />
            </IconButton>
          )}
          <Logo />
          <Typography
            sx={{
              paddingLeft: '10px',
              borderLeft: '2px solid #444',
              color: '#fff',
              letterSpacing: '0.05em',
              fontWeight: 700,
              marginLeft: '10px',
              fontSize: '13px',
              lineHeight: 1,
              textTransform: 'uppercase',
            }}
          >
            V360
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {location?.pathname === '/v360-app/' ? null : <SearchBar />}
          <Box sx={{ flexGrow: 1 }} />

          <Stack
            direction="row"
            gap={1}
            divider={<Divider orientation="vertical" flexItem />}
            color={(theme) => theme.palette.grey[500]}
            alignItems="center"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MarketSelector />
              <IconButton
                onClick={toggleMode}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                }}
                title={isDarkMode ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
              >
                <Icon
                  icon={isDarkMode ? 'mdi:white-balance-sunny' : 'mdi:moon-waning-crescent'}
                  style={{ fontSize: '24px' }}
                />
              </IconButton>
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  fontSize: 12,
                  color: (theme) => theme.palette.grey[700],
                  cursor: 'pointer',
                }}
                aria-label="Notifications of current user"
                aria-controls={profileMenuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
              >
                {userInfo?.name?.split(' ')?.[0]?.[0]?.toUpperCase?.()}
                {userInfo?.name?.split(' ')?.[1]?.[0]?.toUpperCase?.()}
              </Avatar>
            </Box>
          </Stack>
        </Stack>
      </AppBar>
      <Box sx={{ height: 57 }} />
    </>
  );
};

export default Header;
